import { useCallback, useState } from "react";

interface UseHighlyConfidentialValueParams {
  /**
   * 機密値を取得するための関数
   */
  fetchConfidentialValue: () => Promise<string>;
}

interface UseHighlyConfidentialValueReturn {
  /** 秘匿値が表示されているかどうか */
  isRevealed: boolean;
  /** 復号化された秘匿値 */
  secretValue: string | null;
  /** API呼び出し中かどうか */
  isLoading: boolean;
  /** エラーオブジェクト */
  error: Error | null;
  /** 秘匿値の表示/非表示を切り替える */
  toggleReveal: () => Promise<void>;
  /** 秘匿値の状態をリセットする（保存後などに使用） */
  reset: () => void;
}

/**
 * 秘匿値の表示/非表示を管理するカスタムフック（CForm専用）
 */
export const useHighlyConfidentialValue = ({
  fetchConfidentialValue,
}: UseHighlyConfidentialValueParams): UseHighlyConfidentialValueReturn => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [secretValue, setSecretValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const toggleReveal = useCallback(async () => {
    if (isRevealed) {
      // 非表示にする
      setIsRevealed(false);
      setSecretValue(null);
      setError(null);
      return;
    }

    // 表示する（エラーをクリア）
    setError(null);
    setIsLoading(true);
    try {
      const value = await fetchConfidentialValue();
      setSecretValue(value);
      setIsRevealed(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error("Failed to fetch highly confidential value:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isRevealed, fetchConfidentialValue]);

  const reset = useCallback(() => {
    setIsRevealed(false);
    setSecretValue(null);
    setError(null);
  }, []);

  return {
    isRevealed,
    secretValue,
    isLoading,
    error,
    toggleReveal,
    reset,
  };
};
